import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { useDealer } from '../../context/DealerContext';
import { dealerApi, Credit, FoundCustomer } from '../../services/dealerApi';
import Money from '../../components/dealer/Money';
import CustomerLookup from '../../components/dealer/CustomerLookup';
import { dealerErrorMessage, fmt, formatAed, validateWholeAed, NOTE_MAX } from '../../utils/dealer';

/**
 * Spec 085 (Flow D) — assign credit from the dealer balance: LIVE at once (money already received), only to a
 * registered customer found by email + phone first (BR-8, D-152). Whole AED, ≥ 100, ≤ available; a concurrent
 * assignment that drains the balance is 422 INSUFFICIENT_DEALER_BALANCE (BR-22).
 */
export default function AssignCreditPage() {
  const { t } = useLanguage();
  const { token } = useDealer();
  const [available, setAvailable] = useState<string | null>(null);
  const [customer, setCustomer] = useState<FoundCustomer | null>(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [assigned, setAssigned] = useState<Credit | null>(null);

  const loadBalance = useCallback(() => {
    if (!token) return;
    dealerApi.balance(token).then((b) => setAvailable(b.availableAed)).catch((err) => setError(dealerErrorMessage(err, t)));
  }, [token, t]);
  useEffect(() => { loadBalance(); }, [loadBalance]);

  const availableNum = available == null ? 0 : Number(available);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    const amountErr = validateWholeAed(amount, Math.floor(availableNum));
    if (amountErr) errs.amount = amountErr === 'dealer.validation.amountAboveBalance'
      ? fmt(t(amountErr), { amount: formatAed(available) })
      : t(amountErr);
    if (note.length > NOTE_MAX) errs.note = t('dealer.validation.tooLong');
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0 || !token || !customer) return;

    setBusy(true);
    setError(null);
    try {
      const c = await dealerApi.assign({
        customerRef: customer.customerRef,
        buyerEmail: customer.email,
        buyerPhone: customer.phone,
        amountAed: Number(amount),
        note: note.trim() || undefined,
      }, token);
      setAssigned(c);
      loadBalance();
    } catch (err) {
      setError(dealerErrorMessage(err, t));
      loadBalance();
    } finally {
      setBusy(false);
    }
  };

  if (assigned) {
    return (
      <div className="dealer-page">
        <div className="dealer-card dealer-success" role="status">
          <h2>{t('dealer.assign.doneTitle')}</h2>
          <p>{fmt(t('dealer.assign.doneBody'), { amount: formatAed(assigned.faceValueAed), buyer: assigned.buyerName || assigned.buyerPhone || '' })}</p>
          <div className="dealer-actions">
            <button type="button" className="btn btn-primary" onClick={() => {
              setAssigned(null); setCustomer(null); setAmount(''); setNote('');
            }}>{t('dealer.assign.another')}</button>
            <Link className="btn btn-secondary" to="/dealer/credit?tab=assigned">{t('dealer.assign.viewAssigned')}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dealer-page">
      <div className="dealer-page__header">
        <div>
          <h2>{t('dealer.assign.title')}</h2>
          <p className="dealer-muted">{t('dealer.assign.subtitle')}</p>
        </div>
      </div>
      <div className="dealer-stat dealer-stat--wide">
        <span className="dealer-stat__label">{t('dealer.balance.available')}</span>
        <Money className="dealer-stat__value" value={available} />
      </div>
      {available != null && availableNum <= 0 && (
        <div className="dealer-empty">
          <p>{t('dealer.assign.noBalance')}</p>
          <Link className="btn btn-primary" to="/dealer/credit/packs">{t('dealer.credit.buyPackCta')}</Link>
        </div>
      )}
      <div className="dealer-card">
        <CustomerLookup token={token ?? ''} idPrefix="assign-buyer" confirmed={customer}
          onConfirm={setCustomer} onChange={() => setCustomer(null)} />
      </div>
      {!customer && error && <div className="error-message">{error}</div>}
      {customer && (
        <form className="dealer-card dealer-form" onSubmit={submit} noValidate>
          <div className="form-group">
            <label htmlFor="assign-amount">{t('dealer.form.amount')}</label>
            <input id="assign-amount" type="text" inputMode="numeric" dir="ltr" value={amount} onChange={(e) => setAmount(e.target.value)} />
            <div className="dealer-hint">{t('dealer.form.amountHint')}</div>
            {fieldErrors.amount && <div className="dealer-field-error">{fieldErrors.amount}</div>}
          </div>
          <div className="form-group">
            <label htmlFor="assign-note">{t('dealer.form.note')}</label>
            <input id="assign-note" type="text" value={note} maxLength={NOTE_MAX} placeholder={t('dealer.form.notePlaceholder')} onChange={(e) => setNote(e.target.value)} />
            {fieldErrors.note && <div className="dealer-field-error">{fieldErrors.note}</div>}
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" className="btn btn-primary" disabled={busy || available == null}>
            {busy ? t('dealer.common.saving') : t('dealer.assign.submit')}
          </button>
        </form>
      )}
    </div>
  );
}
